import { Fragment, memo, useMemo, useReducer } from 'react';

import { ImageDown, Plus } from 'lucide-react';
import { isNonNullish } from 'remeda';
import { toast } from 'sonner';
import useSWRMutation from 'swr/mutation';

import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

import { SlotForm } from '../../slotFormDialog/form';
import { SlotFormDialog } from '../../slotFormDialog/SlotFormDialog';

import css from './ProviderSlotCard.module.css';

import { slotService } from 'v2/api/slotService';
import { Card } from 'v2/components/card/Card';
import { ImageButton } from 'v2/components/imageButton/ImageButton';
import { ProviderSlotModel } from 'v2/types/SlotModel';
import { downloadFileFromLink, handleHttpError } from 'v2/utils';

interface ProviderSlotCardProps {
  slot: ProviderSlotModel;
  onRevalidate: VoidFunction;
}

export const ProviderSlotCard = memo<ProviderSlotCardProps>(({ slot, onRevalidate }) => {
  const [isOpen, setOpen] = useReducer((state) => !state, false);

  const { trigger, isMutating } = useSWRMutation('createSlot', slotService.createSlot, {
    onSuccess: () => {
      onRevalidate();
      toast.success('New slot has been successfully created');
      setOpen();
    },
    onError: handleHttpError,
  });

  const initialValues: SlotForm = useMemo(
    () => ({
      identifier: slot.gameId,
      title: slot.name,
      providerName: slot.providerName,
      isActive: true,
      order: 1,
      marks: [],
      iconId: '',
      mobileIconId: null,
    }),
    [slot]
  );

  const handleSubmit = (values: SlotForm, resetForm: VoidFunction) => {
    trigger(values).then(() => resetForm());
  };

  const handleDownload = () => {
    if (isNonNullish(slot.logo)) {
      downloadFileFromLink(slot.logo, slot.name);
    }
  };

  return (
    <Fragment>
      <Card className={css.providerSlotCard}>
        <div className={css.providerSlotCardInfo}>
          {isNonNullish(slot.logo) && (
            <ImageButton className={css.providerSlotCardImage} src={slot.logo} alt={slot.name} />
          )}

          <div className={css.providerSlotTitle}>
            <p>{slot.name}</p>

            <Chip label={slot.providerName} size="small" />
          </div>
        </div>

        <div>
          <Tooltip title="Add" placement="top">
            <IconButton onClick={setOpen}>
              <Plus />
            </IconButton>
          </Tooltip>

          {isNonNullish(slot.logo) && (
            <Tooltip title="Download Image" placement="top" onClick={handleDownload}>
              <IconButton onClick={setOpen}>
                <ImageDown width={20} height={20} />
              </IconButton>
            </Tooltip>
          )}
        </div>
      </Card>

      <SlotFormDialog
        title="Add Provider Slot"
        isOpen={isOpen}
        onClose={setOpen}
        initialValues={initialValues}
        isSubmitting={isMutating}
        onSubmit={handleSubmit}
        isProviderSlot
      />
    </Fragment>
  );
});

ProviderSlotCard.displayName = 'ProviderSlotCard';
